import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { extractPluginError } from '@heidi/plugin-core';
import { type Region } from '@heidi/schema/ml-scribe';

/**
 * Metadata for a region-appropriate update returned by the Rust updater.
 * `null` means no update is offered on the resolved channel.
 */
export type RegionUpdateInfo = { version: string; body?: string };

/**
 * Thrown when an updater call rejects. Carries the Rust `error_code` on the
 * regional feed, or the code `classifyGlobalUpdaterError` derives on the global one.
 */
export class UpdaterBridgeError extends Error {
  constructor(
    message: string,
    readonly errorCode?: string
  ) {
    super(message);
    this.name = 'UpdaterBridgeError';
  }
}

async function invokeUpdaterCommand<T>(
  command: string,
  payload: Record<string, unknown>
): Promise<T> {
  if (!isTauri()) {
    throw new UpdaterBridgeError(
      'Updater bridge is only available inside the Tauri runtime'
    );
  }
  const { invoke } = await import('@tauri-apps/api/core');
  try {
    return await invoke<T>(command, payload);
  } catch (error) {
    const { message, errorCode } = extractPluginError(error);
    // Stays `debug`, and deliberately carries no `tag`: the calling model logs
    // this same rejection at `error` with the version and the user's intent
    // attached. Anchoring both would double every Datadog alert and split
    // triage across two signatures for one failure. The model's log is the
    // canonical one; this is breadcrumb detail for a local repro.
    logger.debug('[updater] command failed', { command, message, errorCode });
    throw new UpdaterBridgeError(message, errorCode);
  }
}

/**
 * Region-aware updater bridge (APP-6113). Forwards the validated `Region` (from
 * `kindeProperties.region`); Rust binds the CrabNebula channel from a closed map
 * and rejects unknown regions. Never pass a channel string.
 *
 * Mirrors the chronicle / rpa plugin bridge shape (raw `invoke`, no `ipc_invoke`
 * envelope, no auto-toast) rather than the older `*Controller` class.
 */
export const updaterBridge = {
  checkUpdateForRegion(region: Region): Promise<RegionUpdateInfo | null> {
    return invokeUpdaterCommand<RegionUpdateInfo | null>('check_update_for_region', {
      region,
    });
  },

  /** Resolves true when an update was installed (caller should relaunch). */
  installUpdateForRegion(region: Region): Promise<boolean> {
    return invokeUpdaterCommand<boolean>('install_update_for_region', { region });
  },
};
